/**
 * iter-119w — single source of truth for the Sidebar navigation AND the
 * RBAC matrix categories. When you add/remove/move an entry here, the
 * Sidebar updates AND the Settings → RBAC tab automatically reflects
 * the change (categories + module list both derive from this spec).
 *
 * Every entry that should appear in the RBAC matrix MUST declare a
 * `module` key. Group entries (no `to`) act as Sidebar parents and
 * also gate the whole submenu via their `key` (e.g. `analytics_group`).
 *
 * Backend `settings_routes.MODULES` must stay in sync — new modules
 * added here are picked up by the matrix self-heal on first read, but
 * the default permissions for the role still need to be declared
 * server-side.
 */
import {
    Home, Folder, Database, LayoutDashboard, Images, Sparkles, Users,
    Building2, ShieldCheck, Layers, Settings as SettingsIcon, CreditCard,
    Shield, BarChart3, BookOpenCheck, Scale, Library, Megaphone, Plug,
    PieChart, Boxes, UsersRound, Lightbulb, Wrench, Clock, FileCode, Brush,
    Package, ShoppingBag, ListChecks, Activity, LifeBuoy, Compass, BookOpen,
    HelpCircle, LayoutTemplate, Wand2,
} from "lucide-react";

export const NAV = [
    { to: "/", label: "Home", icon: Home, key: "home", module: "home" },

    { to: "/workspace", label: "Workspace", icon: Folder, key: "workspace", module: "workspace", section: "Product" },
    { to: "/sense-ai", label: "Sense AI", icon: Sparkles, key: "sense_ai", module: "sense_ai", section: "Product" },
    {
        label: "Analytics", icon: PieChart, key: "analytics_group", module: "analytics_group", section: "Product",
        children: [
            { to: "/data",       label: "Data",       icon: Database,        key: "data",       module: "data" },
            { to: "/data-control", label: "Data Control", icon: Shield,       key: "data_control", module: "data_control" },
            { to: "/dashboards", label: "Dashboards", icon: LayoutDashboard, key: "dashboards", module: "dashboards" },
            { to: "/gallery",    label: "Gallery",    icon: Images,          key: "gallery",    module: "gallery" },
            { to: "/app-catalog",label: "App Catalog", icon: ShoppingBag,    key: "app_catalog", module: "app_catalog" },
            { to: "/schedules",  label: "Schedules",  icon: Clock,           key: "schedules",  module: "schedules" },
        ],
    },
    {
        label: "Assets", icon: Boxes, key: "assets_group", module: "assets_group", section: "Assets",
        children: [
            { to: "/data-models",          label: "Data Models",          icon: Layers,   key: "data_models",          module: "data_models" },
            { to: "/playbooks",            label: "Playbooks",            icon: Library,  key: "playbooks",            module: "playbooks" },
            { to: "/platform/connectors",  label: "Platform Connectors",  icon: Plug,     key: "platform_connectors",  module: "platform_connectors" },
            { to: "/platform/templates",   label: "Platform Templates",   icon: LayoutTemplate, key: "platform_templates",   module: "platform_templates" },
            { to: "/yaml-connectors",      label: "YAML Connectors",      icon: FileCode, key: "yaml_connectors",      module: "yaml_connectors" },
            { to: "/app-studio",           label: "App Studio",           icon: Package,  key: "app_studio",           module: "app_studio" },
            { to: "/builder-studio",       label: "Builder Studio",       icon: Wand2,    key: "builder_studio",       module: "builder_studio" },
        ],
    },
    {
        label: "Insights", icon: Lightbulb, key: "insights_group", module: "insights_group", section: "Analytics & Reports",
        children: [
            { to: "/operations", label: "Operations", icon: Shield,    key: "operations", module: "operations" },
            { to: "/reports",    label: "Reports",    icon: BarChart3, key: "reports",    module: "reports" },
        ],
    },
    {
        label: "Team", icon: UsersRound, key: "team_group", module: "team_group", section: "Administration",
        children: [
            { to: "/users",          label: "Users",              icon: Users,       key: "users",          module: "users" },
            { to: "/organizations",  label: "Organizations",      icon: Building2,   key: "organizations",  module: "organizations" },
            { to: "/audit-activity", label: "Activity Log", icon: ShieldCheck, key: "audit_activity", module: "audit_activity" },
        ],
    },
    { to: "/billing", label: "Billing", icon: CreditCard, key: "billing", module: "billing", section: "Administration" },
    {
        label: "Settings", icon: SettingsIcon, key: "settings_group", module: "settings_group", section: "Administration",
        children: [
            { to: "/settings", label: "Administration", icon: Wrench, key: "settings", module: "settings" },
            { to: "/settings/branding", label: "Branding", icon: Brush, key: "branding_page", module: "branding_page" },
            { to: "/settings/tracking", label: "Header & Footer Scripts", icon: FileCode, key: "tracking_page", module: "tracking_page" },
            { to: "/settings/setup-checklist", label: "Setup Checklist", icon: ListChecks, key: "setup_checklist_page", module: "setup_checklist_page" },
        ],
    },
    // Support — END-USER help + product updates, placed right after Administration.
    {
        label: "Help & Guides", icon: LifeBuoy, key: "help_group", module: "help_group", section: "Support",
        children: [
            { to: "/help",          label: "Getting Started", icon: Compass,    key: "help_home",     module: "help_home" },
            { to: "/help/guides",   label: "How-to Guides",   icon: BookOpen,   key: "how_to_guides", module: "how_to_guides" },
            { to: "/help/glossary", label: "Glossary",        icon: Library,    key: "glossary",      module: "glossary" },
            { to: "/help/faq",      label: "FAQ & Help",      icon: HelpCircle, key: "faq_help",      module: "faq_help" },
            { to: "/scope-matrix",  label: "Who sees what",   icon: Shield,     key: "scope_matrix",  module: "scope_matrix" },
            { to: "/billing-guide", label: "Billing & Plans", icon: CreditCard, key: "billing_guide", module: "billing_guide" },
        ],
    },
    { to: "/release-notes", label: "Release Notes", icon: Megaphone, key: "release_notes", module: "release_notes", section: "Support" },
    // Governance — enterprise audit/compliance area. Top-level, SA/OA-only by
    // default (entity roles default to '-' server-side). One `governance`
    // module gates the whole area from the Settings → RBAC tab.
    { to: "/governance", label: "Governance", icon: Scale, key: "governance", module: "governance", section: "Governance" },
    // Admin / Platform — the OPERATOR / platform reference bucket (SA-only).
    {
        label: "Admin / Platform", icon: BookOpenCheck, key: "admin_group", module: "admin_group", section: "Platform",
        children: [
            { to: "/feature-catalog", label: "Feature Catalog", icon: BookOpenCheck, key: "feature_catalog", module: "feature_catalog" },
            { to: "/business-rules",  label: "Business Rules",  icon: Scale,         key: "business_rules",  module: "business_rules" },
            { to: "/documentation",   label: "Skills & Architecture", icon: FileCode, key: "documentation",  module: "documentation" },
        ],
    },
];

/**
 * Flat list of every (group_label, module_id, label) the menu declares,
 * grouped for the RBAC matrix. Group entries appear first in their group
 * so SA can use them as a single submenu kill-switch.
 */
export function categoriesFromNav(nav = NAV) {
    const cats = [];
    for (const item of nav) {
        if (item.children) {
            const mods = [];
            if (item.module) mods.push(item.module);
            for (const c of item.children) if (c.module) mods.push(c.module);
            if (mods.length) cats.push({ key: item.key, label: item.label, mods });
        } else if (item.module) {
            // Leaf-level entries get their own pseudo-category (e.g. Billing
            // sitting at the top level alongside Home / Workspace).
            cats.push({ key: item.key, label: item.label, mods: [item.module] });
        }
    }
    return cats;
}
